'use strict'
import React, { useReducer, useEffect, useCallback, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  IconButton,
  Box,
  CircularProgress,
  Button,
  Link as MuiLink,
  Snackbar,
  Tabs,
  Tab
} from '@material-ui/core'
import { Alert, Autocomplete } from '@material-ui/lab'
import SearchIcon from '@material-ui/icons/Search'
import RefreshIcon from '@material-ui/icons/Refresh'
import SyncIcon from '@material-ui/icons/Sync'
import WarningIcon from '@material-ui/icons/Warning'
import ErrorIcon from '@material-ui/icons/Error'
import TrendingDownIcon from '@material-ui/icons/TrendingDown'
import TableChartIcon from '@material-ui/icons/TableChart'
import MapIcon from '@material-ui/icons/Map'
import EventNoteIcon from '@material-ui/icons/EventNote'
import LockIcon from '@material-ui/icons/Lock'
import { useHistory, useLocation } from 'react-router-dom'
import MapTab from './TerritoryWorkbench/MapTab'
import RoutesTab from './TerritoryWorkbench/RoutesTab'

// Column definitions
const headCells = [
  { id: 'name', numeric: false, label: 'Property Name', width: 200 },
  { id: 'companyName', numeric: false, label: 'Company', width: 180 },
  { id: 'address1', numeric: false, label: 'Address', width: 180 },
  { id: 'completedOccurrences', numeric: true, label: 'Visits', width: 80 },
  { id: 'annualOccurrences', numeric: true, label: 'Annual', width: 80 },
  { id: 'estimatedHours', numeric: true, label: 'Est Hrs', width: 80 },
  { id: 'avgOnSiteHoursDisplay', numeric: true, label: 'Avg On-Site', width: 100 },
  { id: 'medianOnSiteHoursDisplay', numeric: true, label: 'Med On-Site', width: 100 },
  { id: 'onSiteVariance', numeric: true, label: 'Est - Avg', width: 90 },
  { id: 'totalOTHours', numeric: true, label: 'OT Hrs', width: 80 },
  { id: 'estimatedLaborCost', numeric: true, label: 'Est Cost', width: 100 },
  { id: 'totalLaborCost', numeric: true, label: 'Actual Cost', width: 100 },
  { id: 'costVariance', numeric: true, label: 'Cost Var', width: 100 },
  { id: 'totalRevenue', numeric: true, label: 'Revenue', width: 100 },
  { id: 'netProfit', numeric: true, label: 'Net Profit', width: 100 },
  { id: 'profitMargin', numeric: true, label: 'Margin %', width: 90 }
]

// Sorting helpers
function descendingComparator(a, b, orderBy) {
  const aVal = a[orderBy] ?? 0
  const bVal = b[orderBy] ?? 0
  if (bVal < aVal) return -1
  if (bVal > aVal) return 1
  return 0
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map((el) => el[0])
}

// Reducer for complex state management
const initialState = {
  rows: [],
  filteredRows: [],
  loading: true,
  error: null,
  order: 'asc',
  orderBy: 'name',
  page: 0,
  rowsPerPage: 20,
  searchText: '',
  companyFilter: '',
  // Quick filters
  showUnderBidOnly: false,
  showNegativeProfitOnly: false,
  showOvertimeOnly: false,
  // Sync status
  lastSyncedAt: null,
  syncing: false,
  // View mode (table or map)
  viewMode: 'table'
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_ROWS':
      return { ...state, rows: action.payload, filteredRows: action.payload, loading: false }
    case 'SET_FILTERED_ROWS':
      return { ...state, filteredRows: action.payload, page: 0 }
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false }
    case 'SET_ORDER':
      return { ...state, order: action.payload.order, orderBy: action.payload.orderBy }
    case 'SET_PAGE':
      return { ...state, page: action.payload }
    case 'SET_ROWS_PER_PAGE':
      return { ...state, rowsPerPage: action.payload, page: 0 }
    case 'SET_SEARCH_TEXT':
      return { ...state, searchText: action.payload }
    case 'SET_COMPANY_FILTER':
      return { ...state, companyFilter: action.payload, page: 0 }
    case 'TOGGLE_UNDER_BID':
      return { ...state, showUnderBidOnly: !state.showUnderBidOnly }
    case 'TOGGLE_NEGATIVE_PROFIT':
      return { ...state, showNegativeProfitOnly: !state.showNegativeProfitOnly }
    case 'TOGGLE_OVERTIME':
      return { ...state, showOvertimeOnly: !state.showOvertimeOnly }
    case 'SET_SYNC_STATUS':
      return { ...state, lastSyncedAt: action.payload.lastSyncedAt }
    case 'SET_SYNCING':
      return { ...state, syncing: action.payload }
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.payload }
    default:
      return state
  }
}

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    padding: theme.spacing(2)
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2)
  },
  toolbar: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center'
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: theme.spacing(1)
  },
  title: {
    marginRight: theme.spacing(2)
  },
  syncInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  search: {
    padding: theme.spacing(1),
    minWidth: 280
  },
  filterChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  tableWrapper: {
    overflowX: 'auto'
  },
  table: {
    minWidth: 1680
  },
  tableCell: {
    padding: '8px 12px',
    fontSize: '0.85rem'
  },
  headerCell: {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
    whiteSpace: 'nowrap'
  },
  negative: {
    color: theme.palette.error.main,
    fontWeight: 600
  },
  positive: {
    color: theme.palette.success?.main || '#2a9e0d',
    fontWeight: 600
  },
  warning: {
    color: theme.palette.warning?.main || '#ff9800',
    fontWeight: 600
  },
  problemRow: {
    backgroundColor: 'rgba(244, 67, 54, 0.08)'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4)
  },
  statsBar: {
    display: 'flex',
    gap: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2)
  },
  statItem: {
    textAlign: 'center'
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: 600
  },
  statLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary
  },
  syncButton: {
    marginLeft: theme.spacing(1)
  },
  lastSync: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center'
  }
}))

// Format currency
const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

// Format number with decimals
const formatNumber = (value, decimals = 2) => {
  if (value == null || isNaN(value)) return '-'
  return Number(value).toFixed(decimals)
}

// Format percentage
const formatPercent = (value) => {
  if (value == null || isNaN(value)) return '-'
  return `${Number(value).toFixed(1)}%`
}

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const normalizeRoutingRow = (row = {}) => {
  const estimatedHours = Number(row.estimatedHours || 0)
  const avgOnSiteHoursDisplay = Number(row.avgOnSiteHours || row.avgActualHours || 0)
  const medianOnSiteHoursDisplay = Number(row.medianOnSiteHours || row.medianActualHours || 0)
  const companyId = row.companyId || row.CompanyID || null
  const companyName = row.companyName || row.CompanyName || ''
  return {
    ...row,
    companyId,
    companyName,
    companyLabel: companyId
      ? `${companyName || 'Company'} #${companyId}`
      : 'No company',
    estimatedHours,
    avgOnSiteHoursDisplay,
    medianOnSiteHoursDisplay,
    onSiteVariance: estimatedHours - avgOnSiteHoursDisplay
  }
}

const getAspirePropertyId = (row = {}) => row.aspirePropertyId || row.propertyId || row.PropertyID || null

const getAspirePropertyUrl = (row = {}) => {
  const propertyId = getAspirePropertyId(row)
  return propertyId ? `https://cloud.youraspire.com/app/properties/overview/${propertyId}` : null
}

// Table Header Component
function EnhancedTableHead({ classes, order, orderBy, onRequestSort }) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property)
  }

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
            className={`${classes.tableCell} ${classes.headerCell}`}
            style={{ minWidth: headCell.width }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

// Main Component
function RoutingPropertiesTable(props) {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const [state, dispatch] = useReducer(reducer, initialState)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' })
  const [territoryContext, setTerritoryContext] = useState({
    crews: [],
    routes: [],
    loading: false,
    loaded: false
  })

  const territoryCrewIds = useMemo(
    () => territoryContext.crews.map(crew => crew._id).sort(),
    [territoryContext.crews]
  )
  const territoryRouteIds = useMemo(
    () => territoryContext.routes.map(route => route._id).sort(),
    [territoryContext.routes]
  )

  useEffect(() => {
    const requestedView = new URLSearchParams(location.search).get('view')
    if (requestedView === 'map' || requestedView === 'table' || requestedView === 'routes') {
      dispatch({ type: 'SET_VIEW_MODE', payload: requestedView })
    }
  }, [location.search])

  // Fetch data from DB
  const fetchData = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true })
    try {
      const response = await axios.get(`${props.url}/api/aspire/routing/properties`)
      const rows = (response.data || []).map(normalizeRoutingRow)
      dispatch({ type: 'SET_ROWS', payload: rows })
    } catch (error) {
      console.error('Error fetching routing data:', error)
      dispatch({ type: 'SET_ERROR', payload: error.message })
    }
  }, [props.url])

  // Fetch sync status
  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${props.url}/api/aspire/routing/status`)
      dispatch({ type: 'SET_SYNC_STATUS', payload: response.data })
    } catch (error) {
      console.error('Error fetching sync status:', error)
    }
  }, [props.url])

  const fetchTerritoryContext = useCallback(async () => {
    setTerritoryContext(prev => ({ ...prev, loading: true }))
    try {
      const response = await axios.get(`${props.url}/api/hmi/territory/properties`)
      setTerritoryContext({
        crews: response.data?.crews || [],
        routes: response.data?.routes || [],
        loading: false,
        loaded: true
      })
    } catch (error) {
      console.error('Error fetching live territory context:', error)
      setTerritoryContext(prev => ({ ...prev, loading: false, loaded: true }))
      setSnackbar({ open: true, message: 'Failed to load live map crews/routes: ' + error.message, severity: 'error' })
    }
  }, [props.url])

  // Trigger sync from Aspire
  const handleSync = async () => {
    dispatch({ type: 'SET_SYNCING', payload: true })
    setSnackbar({ open: true, message: 'Syncing data from Aspire... This may take a few minutes.', severity: 'info' })

    try {
      await axios.post(`${props.url}/api/aspire/routing/sync`)
      // Poll for completion by checking if data has changed
      // For now just wait a bit and refresh
      setTimeout(async () => {
        await fetchData()
        await fetchSyncStatus()
        dispatch({ type: 'SET_SYNCING', payload: false })
        setSnackbar({ open: true, message: 'Sync complete! Data has been refreshed.', severity: 'success' })
      }, 5000)
    } catch (error) {
      console.error('Error syncing:', error)
      dispatch({ type: 'SET_SYNCING', payload: false })
      setSnackbar({ open: true, message: 'Sync failed: ' + error.message, severity: 'error' })
    }
  }

  useEffect(() => {
    fetchData()
    fetchSyncStatus()
  }, [fetchData, fetchSyncStatus])

  useEffect(() => {
    if (state.viewMode === 'map' && !territoryContext.loaded && !territoryContext.loading) {
      fetchTerritoryContext()
    }
  }, [fetchTerritoryContext, state.viewMode, territoryContext.loaded, territoryContext.loading])

  // Apply filters
  useEffect(() => {
    let filtered = [...state.rows]

    // Text search
    if (state.searchText) {
      const search = state.searchText.toLowerCase()
      filtered = filtered.filter(row =>
        (row.name && row.name.toLowerCase().includes(search)) ||
        (row.address1 && row.address1.toLowerCase().includes(search)) ||
        (row.companyName && row.companyName.toLowerCase().includes(search)) ||
        (row.companyId && String(row.companyId).includes(search))
      )
    }

    if (state.companyFilter) {
      filtered = filtered.filter(row => String(row.companyId || '') === state.companyFilter)
    }

    // Quick filters
    if (state.showUnderBidOnly) {
      filtered = filtered.filter(row => row.onSiteVariance < 0)
    }
    if (state.showNegativeProfitOnly) {
      filtered = filtered.filter(row => row.netProfit < 0)
    }
    if (state.showOvertimeOnly) {
      filtered = filtered.filter(row => row.totalOTHours > 0)
    }

    dispatch({ type: 'SET_FILTERED_ROWS', payload: filtered })
  }, [state.rows, state.searchText, state.companyFilter, state.showUnderBidOnly, state.showNegativeProfitOnly, state.showOvertimeOnly])

  // Handlers
  const handleRequestSort = (event, property) => {
    const isAsc = state.orderBy === property && state.order === 'asc'
    dispatch({ type: 'SET_ORDER', payload: { order: isAsc ? 'desc' : 'asc', orderBy: property } })
  }

  const handleChangePage = (event, newPage) => {
    dispatch({ type: 'SET_PAGE', payload: newPage })
  }

  const handleChangeRowsPerPage = (event) => {
    dispatch({ type: 'SET_ROWS_PER_PAGE', payload: parseInt(event.target.value, 10) })
  }

  const handleSearchChange = (event) => {
    dispatch({ type: 'SET_SEARCH_TEXT', payload: event.target.value })
  }

  const handleCompanyFilterChange = (event) => {
    dispatch({ type: 'SET_COMPANY_FILTER', payload: event.target.value })
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  const handleMapSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity })
  }, [])

  const companyOptions = useMemo(() => {
    const byId = new Map()
    state.rows.forEach(row => {
      const key = row.companyId ? String(row.companyId) : ''
      if (!key) return
      if (!byId.has(key)) {
        byId.set(key, {
          id: key,
          name: row.companyName || `Company #${key}`,
          label: `${row.companyName || 'Company'} #${key}`,
          count: 0
        })
      }
      byId.get(key).count += 1
    })
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [state.rows])

  // Calculate summary stats
  const totalProperties = state.filteredRows.length
  const problemProperties = state.filteredRows.filter(r => r.netProfit < 0).length
  const underBidProperties = state.filteredRows.filter(r => r.onSiteVariance < 0).length
  const totalRevenue = state.filteredRows.reduce((sum, r) => sum + (r.totalRevenue || 0), 0)
  const totalProfit = state.filteredRows.reduce((sum, r) => sum + (r.netProfit || 0), 0)
  const selectedCompany = state.companyFilter
    ? companyOptions.find(company => company.id === state.companyFilter)
    : null

  // Get value class based on positive/negative
  const getValueClass = (value, inverse = false) => {
    if (value == null || value === 0) return ''
    if (inverse) {
      return value > 0 ? classes.negative : classes.positive
    }
    return value < 0 ? classes.negative : classes.positive
  }

  // Check if row has problems
  const isRowProblematic = (row) => {
    return row.netProfit < 0 || row.onSiteVariance < -2 || row.profitMargin < -50
  }

  if (state.loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    )
  }

  if (state.error) {
    return (
      <Paper className={classes.paper}>
        <Box p={3} textAlign="center">
          <ErrorIcon color="error" style={{ fontSize: 48 }} />
          <Typography color="error">Error loading data: {state.error}</Typography>
        </Box>
      </Paper>
    )
  }

  const sortedRows = stableSort(state.filteredRows, getComparator(state.order, state.orderBy))
  const paginatedRows = sortedRows.slice(
    state.page * state.rowsPerPage,
    state.page * state.rowsPerPage + state.rowsPerPage
  )

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <Toolbar className={classes.toolbar}>
          <div style={{ width: '100%' }}>
            {/* Title and Sync Button */}
            <div className={classes.titleRow}>
              <Box display="flex" alignItems="center">
                <Typography className={classes.title} variant="h5" component="div">
                  Routing Analysis - Property Performance
                </Typography>
                <Tabs
                  value={state.viewMode}
                  onChange={(e, v) => dispatch({ type: 'SET_VIEW_MODE', payload: v })}
                  indicatorColor="primary"
                  textColor="primary"
                  style={{ marginLeft: 24 }}
                >
                  <Tab value="table" icon={<TableChartIcon />} label="Table" />
                  <Tab value="map" icon={<MapIcon />} label="Map" />
                  <Tab value="routes" icon={<EventNoteIcon />} label="Routes" />
                </Tabs>
              </Box>
              <div className={classes.syncInfo} style={{ marginLeft: 'auto' }}>
                <Typography className={classes.lastSync}>
                  Last synced: {formatDate(state.lastSyncedAt)}
                </Typography>
                <Button
                  variant="outlined"
                  color="default"
                  size="small"
                  startIcon={<EventNoteIcon />}
                  onClick={() => history.push('/routing/schedule')}
                  style={{ marginRight: 8 }}
                >
                  View Schedule
                </Button>
                <Button
                  variant="outlined"
                  color="default"
                  size="small"
                  startIcon={<LockIcon />}
                  onClick={() => history.push('/routing/territory/dashboard')}
                  style={{ marginRight: 8 }}
                >
                  Scenario Dashboard
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={state.syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                  onClick={handleSync}
                  disabled={state.syncing}
                  className={classes.syncButton}
                >
                  {state.syncing ? 'Syncing...' : 'Sync Aspire Data'}
                </Button>
              </div>
            </div>

            {/* Summary Stats - Table view only */}
            {state.viewMode === 'table' && (
              <>
                <div className={classes.statsBar}>
                  <div className={classes.statItem}>
                    <div className={classes.statValue}>{totalProperties}</div>
                    <div className={classes.statLabel}>Properties</div>
                  </div>
                  <div className={classes.statItem}>
                    <div className={`${classes.statValue} ${classes.negative}`}>{problemProperties}</div>
                    <div className={classes.statLabel}>Losing Money</div>
                  </div>
                  <div className={classes.statItem}>
                    <div className={`${classes.statValue} ${classes.warning}`}>{underBidProperties}</div>
                    <div className={classes.statLabel}>Under-Bid</div>
                  </div>
                  <div className={classes.statItem}>
                    <div className={classes.statValue}>{formatCurrency(totalRevenue)}</div>
                    <div className={classes.statLabel}>Total Revenue</div>
                  </div>
                  <div className={classes.statItem}>
                    <div className={`${classes.statValue} ${totalProfit < 0 ? classes.negative : classes.positive}`}>
                      {formatCurrency(totalProfit)}
                    </div>
                    <div className={classes.statLabel}>Gross Profit (NI Herbicide/Irrigation)</div>
                  </div>
                </div>
                {selectedCompany && (
                  <Box mb={1}>
                    <Chip
                      label={`Company: ${selectedCompany.label} (${totalProperties} properties)`}
                      onDelete={() => dispatch({ type: 'SET_COMPANY_FILTER', payload: '' })}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                )}

                {/* Search and Filters */}
                <div className={classes.searchContainer}>
                  <TextField
                    className={classes.search}
                    variant="outlined"
                    size="small"
                    placeholder="Search properties..."
                    value={state.searchText}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                  <Autocomplete
                    size="small"
                    options={companyOptions}
                    value={companyOptions.find(company => company.id === state.companyFilter) || null}
                    onChange={(event, option) => handleCompanyFilterChange({ target: { value: option?.id || '' } })}
                    getOptionLabel={(option) => option ? `${option.label} (${option.count})` : ''}
                    getOptionSelected={(option, value) => option.id === value.id}
                    style={{ minWidth: 320 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Company"
                        variant="outlined"
                        placeholder="All companies"
                      />
                    )}
                  />
                  <Tooltip title="Refresh from database">
                    <IconButton onClick={fetchData} size="small">
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </div>

                {/* Quick Filter Chips */}
                <div className={classes.filterChips}>
                  <Chip
                    icon={<TrendingDownIcon />}
                    label="Under-Bid Properties"
                    color={state.showUnderBidOnly ? 'secondary' : 'default'}
                    onClick={() => dispatch({ type: 'TOGGLE_UNDER_BID' })}
                    variant={state.showUnderBidOnly ? 'default' : 'outlined'}
                  />
                  <Chip
                    icon={<ErrorIcon />}
                    label="Negative Profit"
                    color={state.showNegativeProfitOnly ? 'secondary' : 'default'}
                    onClick={() => dispatch({ type: 'TOGGLE_NEGATIVE_PROFIT' })}
                    variant={state.showNegativeProfitOnly ? 'default' : 'outlined'}
                  />
                  <Chip
                    icon={<WarningIcon />}
                    label="Has Overtime"
                    color={state.showOvertimeOnly ? 'secondary' : 'default'}
                    onClick={() => dispatch({ type: 'TOGGLE_OVERTIME' })}
                    variant={state.showOvertimeOnly ? 'default' : 'outlined'}
                  />
                  <Typography variant="body2" color="textSecondary" style={{ marginLeft: 'auto' }}>
                    {state.filteredRows.length} / {state.rows.length} properties
                  </Typography>
                </div>
              </>
            )}
          </div>
        </Toolbar>

        {/* View Mode Content */}
        {state.viewMode === 'map' ? (
          <Box p={2}>
            {territoryContext.loading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress />
              </div>
            ) : (
              <MapTab
                url={props.url}
                selectedScenarioId={null}
                crews={territoryContext.crews}
                crewIds={territoryCrewIds}
                routes={territoryContext.routes}
                routeIds={territoryRouteIds}
                onSnackbar={handleMapSnackbar}
              />
            )}
          </Box>
        ) : state.viewMode === 'routes' ? (
          <Box p={2}>
            <RoutesTab
              url={props.url}
              onSnackbar={handleMapSnackbar}
              onRoutesChanged={() => {
                setTerritoryContext(prev => ({ ...prev, loaded: false }))
                fetchTerritoryContext()
              }}
            />
          </Box>
        ) : (
          <>
            {/* Empty State */}
            {state.rows.length === 0 ? (
              <div className={classes.emptyState}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No property data yet
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Click "Sync Aspire Data" to fetch property performance metrics from Aspire.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={state.syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                  onClick={handleSync}
                  disabled={state.syncing}
                >
                  {state.syncing ? 'Syncing...' : 'Sync Aspire Data'}
                </Button>
              </div>
            ) : (
              <>
                <div className={classes.tableWrapper}>
                  <Table className={classes.table} size="small">
                    <EnhancedTableHead
                      classes={classes}
                      order={state.order}
                      orderBy={state.orderBy}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      {paginatedRows.map((row, index) => (
                        <TableRow
                          key={row.propertyId || row._id || index}
                          hover
                          className={isRowProblematic(row) ? classes.problemRow : ''}
                        >
                          <TableCell className={classes.tableCell}>
                            <Box display="flex" alignItems="center" gap={1}>
                              {isRowProblematic(row) && (
                                <Tooltip title="Problem property - review pricing">
                                  <WarningIcon fontSize="small" color="error" />
                                </Tooltip>
                              )}
                              {getAspirePropertyUrl(row) ? (
                                <MuiLink
                                  href={getAspirePropertyUrl(row)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(event) => event.stopPropagation()}
                                  style={{ color: '#769ba2' }}
                                >
                                  {row.name || '-'}
                                </MuiLink>
                              ) : (
                                row.name || '-'
                              )}
                            </Box>
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            {row.companyId
                              ? `${row.companyName || 'Company'} #${row.companyId}`
                              : '-'}
                          </TableCell>
                          <TableCell className={classes.tableCell}>{row.address1 || '-'}</TableCell>
                          <TableCell className={classes.tableCell} align="right">{row.completedOccurrences || 0}</TableCell>
                          <TableCell className={classes.tableCell} align="right">{row.annualOccurrences || 0}</TableCell>
                          <TableCell className={classes.tableCell} align="right">{formatNumber(row.estimatedHours)}</TableCell>
                          <TableCell className={classes.tableCell} align="right">{formatNumber(row.avgOnSiteHoursDisplay)}</TableCell>
                          <TableCell className={classes.tableCell} align="right">{formatNumber(row.medianOnSiteHoursDisplay)}</TableCell>
                          <TableCell className={classes.tableCell} align="right">
                            <span className={getValueClass(row.onSiteVariance)}>
                              {formatNumber(row.onSiteVariance)}
                            </span>
                          </TableCell>
                          <TableCell className={classes.tableCell} align="right">
                            <span className={row.totalOTHours > 0 ? classes.warning : ''}>
                              {formatNumber(row.totalOTHours)}
                            </span>
                          </TableCell>
                          <TableCell className={classes.tableCell} align="right">{formatCurrency(row.estimatedLaborCost)}</TableCell>
                          <TableCell className={classes.tableCell} align="right">{formatCurrency(row.totalLaborCost)}</TableCell>
                          <TableCell className={classes.tableCell} align="right">
                            <span className={getValueClass(row.costVariance)}>
                              {formatCurrency(row.costVariance)}
                            </span>
                          </TableCell>
                          <TableCell className={classes.tableCell} align="right">{formatCurrency(row.totalRevenue)}</TableCell>
                          <TableCell className={classes.tableCell} align="right">
                            <span className={getValueClass(row.netProfit)}>
                              {formatCurrency(row.netProfit)}
                            </span>
                          </TableCell>
                          <TableCell className={classes.tableCell} align="right">
                            <span className={getValueClass(row.profitMargin)}>
                              {formatPercent(row.profitMargin)}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                      {paginatedRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={headCells.length} align="center">
                            <Typography color="textSecondary">No properties match your filters</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                <TablePagination
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  component="div"
                  count={state.filteredRows.length}
                  rowsPerPage={state.rowsPerPage}
                  page={state.page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </>
        )}
      </Paper>

      {/* Snackbar for sync notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  authenticated: state.authenticated
})

export default connect(mapStateToProps)(RoutingPropertiesTable)
